import en from './en.json';
import ko from './ko.json';

import { getLocale, localeSwitchTarget, writeStoredLocale, type SupportedLocale } from './locale';

export type I18nKey = keyof typeof en;
type Translations = Record<string, string>;

const TRANSLATIONS: Record<string, Translations> = { en, ko };

/**
 * TRAD-3662 — 로케일 저장소·협상 로직은 카탈로그 json 을 끌어오지 않는 `./locale` 로 옮겼다
 * (shared/api 요청 인터셉터가 Accept-Language 를 붙이려고 읽는다). 기존 import 경로 유지를 위해 재export.
 */
export {
  LOCALE_STORAGE_KEY,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  isSupportedLocale,
  resolveLocale,
  readStoredLocale,
  writeStoredLocale,
  getLocale,
  pathLocale,
  localeSwitchTarget,
  __resetStorageErrorLogForTest,
} from './locale';
export type { SupportedLocale } from './locale';

/**
 * 사용자 선택 로케일을 저장한다. 저장 성공 여부를 반환(실패 시 호출부가 reload 를 건너뛰도록).
 * 사이드 이펙트(스토리지 쓰기)만 담당하고 reload 는 호출부가 결정 — 테스트 가능성 확보.
 */
/**
 * 🔴 TRAD-3253 — `<html lang>` 을 현재 로케일에 맞춘다.
 *
 * `index.html` 이 `lang="ko"` 로 **고정**돼 있고 아무도 갱신하지 않아서, EN 을 골라도
 * `document.documentElement.lang === 'ko'` 였다(2026-09-08 `/terms`·`/support` 실측).
 * 결과: **검색엔진이 영어 본문을 한국어로 색인하고, 스크린리더가 영문을 한국어 음성으로 읽는다.**
 *
 * 🔴 **정적 HTML 의 `lang="ko"` 는 버그가 아니다 — 고치지 말 것.** `prerender.mjs:662` 가
 * `locale: 'ko-KR'` 로 렌더해서 **프리렌더 산출물의 본문 자체가 한국어**다(TRAD-2407).
 * 즉 정적 파일에서는 `ko` 가 **맞고**, 어긋나는 것은 **하이드레이션 이후 EN 사용자**뿐이다.
 * 프리렌더를 `en` 으로 바꾸면 한국어 본문에 `lang="en"` 이 붙어 지금보다 나빠진다.
 */
export function syncDocumentLang(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = getLocale();
}

export function setLocale(locale: SupportedLocale): boolean {
  if (!writeStoredLocale(locale)) return false;
  // 🔴 저장 직후 문서 언어도 같이 옮긴다 — 토글로 바꾼 뒤 새로고침 전까지 어긋나면
  //    그 창에서 스크린리더가 계속 이전 언어로 읽는다.
  syncDocumentLang();
  return true;
}

/**
 * TRAD-3921 — 언어 선택을 저장한 «뒤» 화면에 반영한다(부수효과 경계 — 헤더 토글·인사이트 안내 띠 공용).
 * 종전: 제자리 reload. `/en/…`(주소가 언어를 정하는 경로)에서 다른 언어를 골랐으면 접두사를 뗀 주소로 간다 —
 * 제자리 reload 는 주소가 다시 영어를 강제해 아무 일도 하지 않는다(`localeSwitchTarget`).
 */
export function reloadForLocale(next: SupportedLocale): void {
  const { pathname, search, hash } = window.location;
  const target = localeSwitchTarget(pathname, search, hash, next);
  if (target === null) window.location.reload();
  else window.location.assign(target);
}

/**
 * 브라우저 로케일 기반 번역 함수.
 * 매핑이 없으면 en 폴백 → 키 그대로 반환.
 * params 지정 시 번역문의 {key} 플레이스홀더를 치환.
 */
export function t(key: I18nKey, params?: Record<string, string>): string {
  const locale = getLocale();
  let value = TRANSLATIONS[locale]?.[key] ?? TRANSLATIONS['en']?.[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      value = value.split(`{${k}}`).join(v);
    }
  }
  return value;
}

/**
 * TRAD-3640 — 복수형 키의 «기본 이름». 카탈로그에 `<base>_other` 가 있는 base 만 허용한다
 * (없는 base 를 tp() 에 넘기면 컴파일 에러). `_one` 은 선택 — 없으면 `_other` 로 폴백.
 *
 * 🔴 주의: `financial_calendar.filter.country_other` 처럼 «기타»라는 뜻의 `_other` 도 이 타입에 걸린다.
 * 그걸 tp() 로 부르면 의미가 틀리지만 타입으로는 못 막는다 — 복수형 키는 반드시 `_one` 짝과 함께 만든다.
 */
export type PluralBase = { [K in I18nKey]: K extends `${infer B}_other` ? B : never }[I18nKey];

const pluralRulesCache = new Map<string, Intl.PluralRules>();

function pluralRulesFor(locale: string): Intl.PluralRules {
  let rules = pluralRulesCache.get(locale);
  if (!rules) {
    rules = new Intl.PluralRules(locale);
    pluralRulesCache.set(locale, rules);
  }
  return rules;
}

/**
 * TRAD-3640 — 개수에 따라 단수/복수 문구를 고르는 t().
 *
 * - 선택은 «원시 count(number)» 로 한다 — `Intl.PluralRules(locale).select(count)` → `<base>_<category>`
 *   (en: 1 → one, 0·2·1.5 → other / ko: 항상 other). 그 카테고리 키가 없으면 `<base>_other`.
 * - 표시는 호출부가 이미 포맷한 문자열을 params 로 넘긴다(천단위 구분 등 기존 표시 그대로).
 *   🔴 선택 숫자와 표시 숫자를 분리한 이유: «1,000» 같은 표시 문자열로는 복수 판정을 할 수 없다.
 * - 종전 선례(TRAD-2485 StaleBadge · TRAD-2845 StreakIndicator 의 `=== 1 ? _one : _other` 호출부 분기)를
 *   이 함수 하나로 모은다. ko 카탈로그도 parity 때문에 `_one` 을 두지만 값은 `_other` 와 같다.
 */
export function tp(base: PluralBase, count: number, params?: Record<string, string>): string {
  return t(tpKey(base, count), params);
}

/**
 * TRAD-3640 — tp() 의 «키 선택»만. 번역 함수를 주입받는 순수 모델(institutions summary 의 `translate` 등)이나
 * `descKey` 처럼 키를 돌려주는 자리에서 쓴다 — 거기서 t() 를 직접 부르면 주입 경계가 깨진다.
 */
export function tpKey(base: PluralBase, count: number): I18nKey {
  return pluralKey(base, pluralRulesFor(getLocale()).select(count), k => k in en) as I18nKey;
}

/**
 * TRAD-3640 — 순수 함수: 복수 카테고리 → 카탈로그 키. 그 카테고리 키가 없으면 `<base>_other`.
 * (카테고리 키 존재 여부를 주입받아 카탈로그 없이 단위 테스트한다.)
 */
export function pluralKey(base: string, category: Intl.LDMLPluralRule, hasKey: (key: string) => boolean): string {
  const candidate = `${base}_${category}`;
  return hasKey(candidate) ? candidate : `${base}_other`;
}

/**
 * 카탈로그에 해당 키가 실제로 존재하는지 확인하는 타입가드.
 * 백엔드가 동적으로 내려주는 키(예: TRAD-2071 이벤트 titleKey)를 안전하게 t()로 렌더하기 전
 * 존재 여부를 먼저 검증한다 — true여야 t(key)가 실제 번역문을 반환한다고 보장(카탈로그 누락 시 원문 키
 * 노출 방지).
 */
export function hasTranslation(key: string): key is I18nKey {
  return key in en;
}
